import {
  ChallengeDto,
  LicenseValidatorPublicKey,
  SignedChallengeDTO,
} from 'business/models/Models';
import { utilityConfig, UtilityEndpoints } from './config';
import { getConfig, loadConfig } from 'config';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { showAlertSnackBar } from 'utils/Global';

const DEFAULT_LICENSE_AUTHENTICATOR_PORT = 5023;

let hasShownConfigLoadError = false;

const getLicenseAuthenticatorPort = async (): Promise<number> => {
  try {
    // App-level config load may still be in flight (e.g. this gets called from
    // an effect that mounts before/alongside App's own config-loading effect).
    // Await the shared promise instead of racing getConfig()'s synchronous throw.
    await loadConfig();
    return getConfig().LicenseAuthenticatorPort;
  } catch (e) {
    console.error(
      'Error reading license validator config, falling back to default port: ',
      e,
    );
    if (!hasShownConfigLoadError) {
      hasShownConfigLoadError = true;
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Failed to load application configuration. Using default license authenticator port.',
      );
    }
    return DEFAULT_LICENSE_AUTHENTICATOR_PORT;
  }
};

const getLicenseValidatorBaseUrl = async (): Promise<string> => {
  const licenseAuthenticatorPort = await getLicenseAuthenticatorPort();
  const licenseValidatorBaseUrl = `${utilityConfig.protocol}://${utilityConfig.host}:${licenseAuthenticatorPort}`;
  return licenseValidatorBaseUrl;
};

// ---------- Internal helper ----------
async function utilityFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), utilityConfig.timeoutMs);

  try {
    const res = await fetch(`${await getLicenseValidatorBaseUrl()}${path}`, {
      ...options,
      signal: controller.signal,
    });

    if (!res.ok) {
      throw new Error(`Utility error (${res.status})`);
    }

    return (await res.json()) as T;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ---------- Public API ----------
export const licenseValidatorApi = {
  getPublicKey(): Promise<LicenseValidatorPublicKey> {
    return utilityFetch<LicenseValidatorPublicKey>(UtilityEndpoints.publicKey);
  },

  signChallenge(payload: ChallengeDto): Promise<SignedChallengeDTO> {
    return utilityFetch<SignedChallengeDTO>(UtilityEndpoints.getPcInfo, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  getHealth(): Promise<HealthStatus> {
    return utilityFetch<HealthStatus>(UtilityEndpoints.health);
  },
};

export interface HealthStatus {
  status: string;
}
